import React, { useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
} from "../../../design-system";
import { api as apiConfig } from "../../../config";
import { formatInstant } from "../../../helpers/datetime";

/**
 * Verificador público de exports firmados por Kronaly.
 *
 * Caso de uso: el inspector (u otro tercero) recibe un export con un
 * `signature_id` en su manifest y quiere confirmar que el documento es el
 * que se generó originalmente y no fue alterado después. Entra a esta
 * pantalla con su token de Inspección (cualquier token activo del mismo
 * workspace sirve) y el signature_id; el servidor recalcula el HMAC del
 * manifest y devuelve `valid: true|false`.
 *
 * Pública (no requiere login del workspace). El propio token de Inspección
 * actúa como credencial.
 */

// Instancia axios SIN interceptors para no añadir JWT ni X-Workspace-Id:
// la auth aquí es exclusivamente el header X-Inspection-Token.
const plainAxios = axios.create({
  baseURL: apiConfig.API_URL,
  withCredentials: false,
});

function PageWrap({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--kr-bg-app, #f6f7f9)",
        display: "flex",
        justifyContent: "center",
        padding: "40px 16px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 720 }}>{children}</div>
    </div>
  );
}

export default function VerificarExportPage() {
  document.title = "Verificar export | Kronaly";

  const [token, setToken] = useState("");
  const [signatureId, setSignatureId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { valid, manifest, signature_hmac_sha256 } | null
  const [error, setError] = useState(null);

  const verify = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);
    if (!token.trim() || !signatureId.trim()) return;

    setLoading(true);
    try {
      const res = await plainAxios.get(
        `/inspection/exports/${encodeURIComponent(signatureId.trim())}/verify`,
        {
          headers: { "X-Inspection-Token": token.trim() },
        }
      );
      const payload = res?.data?.data || res?.data;
      setResult(payload);
    } catch (err) {
      const status = err?.response?.status;
      const apiError = err?.response?.data?.error;
      if (status === 401) {
        setError(apiError?.message || "Token de inspección inválido, expirado o revocado.");
      } else if (status === 404) {
        setError("La firma no existe o no pertenece al workspace de este token.");
      } else {
        setError(apiError?.message || err?.message || "No se pudo verificar el export.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap>
      <PageHeader
        eyebrow="Inspección de Trabajo"
        title="Verificar export firmado"
        description="Confirma que un export descargado de Kronaly es auténtico y no ha sido modificado. Necesitas el token de Inspección que se te entregó y el signature_id que figura en el manifest del export."
      />

      <Card>
        <form onSubmit={verify} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Token de Inspección" required description="Empieza por kins_. Cualquier token activo del workspace propietario sirve.">
            <Input
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="kins_…"
              autoFocus
              autoComplete="off"
            />
          </Field>

          <Field label="Signature ID" required description="UUID que aparece en el manifest del export como signature_id.">
            <Input
              value={signatureId}
              onChange={(e) => setSignatureId(e.target.value)}
              placeholder="0190a3c2-…-…-…-…"
              autoComplete="off"
            />
          </Field>

          <div>
            <Button
              type="submit"
              variant="primary"
              icon="ShieldCheck"
              disabled={!token.trim() || !signatureId.trim() || loading}
            >
              {loading ? "Verificando…" : "Verificar"}
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <div style={{ marginTop: 16 }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <Result result={result} />
        </div>
      )}
    </PageWrap>
  );
}

function Result({ result }) {
  const m = result.manifest || {};
  const valid = !!result.valid;
  const tz = result.timezone; // zona del workspace (campo hermano del manifest)
  return (
    <Card>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        {valid ? (
          <Badge tone="success" dot>Firma válida</Badge>
        ) : (
          <Badge tone="danger" dot>Firma alterada</Badge>
        )}
        <span style={{ color: "var(--kr-fg-muted)", fontSize: 13 }}>
          Recalculado HMAC-SHA256 sobre el manifest reconstruido desde la base de datos.
        </span>
      </div>

      {!valid && (
        <Alert tone="danger">
          La HMAC almacenada no coincide con el manifest. El export no debe considerarse
          auténtico — o bien el manifest fue manipulado, o bien los datos del export
          original fueron modificados en el servidor.
        </Alert>
      )}

      <div style={metaGrid}>
        <Meta label="Signature ID" value={m.signature_id} mono />
        <Meta label="Generado" value={formatDateTime(m.generated_at, tz)} />
        <Meta label="Workspace" value={m.workspace_id} mono />
        <Meta label="Token usado" value={m.inspection_token_id} mono />
        <Meta label="Rango" value={m.range ? `${m.range.from} → ${m.range.to}` : "—"} />
        <Meta label="Formato" value={(m.format || "—").toUpperCase()} />
        <Meta label="Filas" value={m.rows_count != null ? String(m.rows_count) : "—"} />
        <Meta label="Hash contenido" value={m.content_hash_sha256} mono />
        <Meta label="Hash cadena audit" value={m.audit_chain_head} mono />
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={metaLabel}>Signature HMAC-SHA256</div>
        <div style={hmacBox}>{result.signature_hmac_sha256}</div>
      </div>
    </Card>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div style={metaLabel}>{label}</div>
      <div style={{ ...metaValue, ...(mono ? monoStyle : {}) }}>{value || "—"}</div>
    </div>
  );
}

// Instante en la zona del workspace (tz llega como campo hermano del manifest).
function formatDateTime(iso, tz) {
  return formatInstant(iso, tz, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const metaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
};

const metaLabel = {
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
  marginBottom: 4,
};

const metaValue = {
  fontSize: 14,
  color: "var(--kr-fg-strong)",
};

const monoStyle = {
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  wordBreak: "break-all",
};

const hmacBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
  padding: 12,
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  wordBreak: "break-all",
};
